import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Shield, Ban, TestTube } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { UserDetail } from '../types';

const PROTECTED_ADMIN_EMAIL = 'justus.wiedemann@celsios.io';

export function SettingsPanel({ detail, onChange }: { detail: UserDetail; onChange: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [banReason, setBanReason] = useState('');
  const isProtected = detail.user.email === PROTECTED_ADMIN_EMAIL;

  const toggleAdmin = async () => {
    if (isProtected && detail.is_admin) {
      toast({
        title: 'Protected account',
        description: 'Cannot remove admin from protected account',
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-user-role', {
        body: {
          target_user_id: detail.user.id,
          action: detail.is_admin ? 'remove_admin' : 'add_admin',
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({
        title: 'Role updated',
        description: `${detail.user.email} ${detail.is_admin ? 'is no longer admin' : 'is now admin'}`,
      });
      onChange();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleTestStripe = async () => {
    setBusy(true);
    try {
      const enabled = !(detail.settings?.use_test_stripe ?? false);
      const { data, error } = await supabase.functions.invoke('toggle-test-stripe', {
        body: { target_user_id: detail.user.id, enabled },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({
        title: 'Test Stripe updated',
        description: `Test mode ${enabled ? 'enabled' : 'disabled'}`,
      });
      onChange();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const banUser = async () => {
    if (isProtected) {
      toast({
        title: 'Protected account',
        description: 'Cannot ban protected account',
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('banned_users').insert({
        user_id: detail.user.id,
        banned_by: user.id,
        reason: banReason.trim() || null,
      });
      if (error) throw error;
      toast({ title: 'User banned', description: detail.user.email, variant: 'destructive' });
      setBanReason('');
      onChange();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const unbanUser = async () => {
    setBusy(true);
    try {
      const { error } = await supabase
        .from('banned_users')
        .delete()
        .eq('user_id', detail.user.id);
      if (error) throw error;
      toast({ title: 'User unbanned', description: detail.user.email });
      onChange();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <SettingRow
        icon={<Shield className="h-4 w-4" />}
        iconTone="primary"
        title="Admin role"
        description="Grant full admin access to the dashboard."
        right={
          <div className="flex items-center gap-2">
            {isProtected && (
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
                Protected
              </Badge>
            )}
            <Switch
              checked={detail.is_admin}
              onCheckedChange={toggleAdmin}
              disabled={busy || (isProtected && detail.is_admin)}
            />
          </div>
        }
      />

      <SettingRow
        icon={<TestTube className="h-4 w-4" />}
        iconTone="warning"
        title="Test Stripe mode"
        description="Use test Stripe keys for this user's checkout."
        right={
          <Switch
            checked={detail.settings?.use_test_stripe ?? false}
            onCheckedChange={toggleTestStripe}
            disabled={busy}
          />
        }
      />

      <SettingRow
        icon={<Ban className="h-4 w-4" />}
        iconTone={detail.is_banned ? 'destructive' : 'default'}
        title="Account status"
        description={
          detail.is_banned
            ? `Banned${detail.banned?.reason ? `: ${detail.banned.reason}` : ''}`
            : 'Active'
        }
        right={
          detail.is_banned ? (
            <Button variant="outline" size="sm" onClick={unbanUser} disabled={busy}>
              Unban
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={busy || isProtected}>
                  Ban user
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ban {detail.user.email}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The user will be blocked from all backend operations. They can be unbanned
                    later.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2">
                  <Label>Reason (optional)</Label>
                  <Textarea
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    rows={2}
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={banUser}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Ban
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        }
      />
    </div>
  );
}

function SettingRow({
  icon,
  iconTone = 'default',
  title,
  description,
  right,
}: {
  icon: React.ReactNode;
  iconTone?: 'default' | 'primary' | 'warning' | 'destructive';
  title: string;
  description: string;
  right: React.ReactNode;
}) {
  const toneClass = {
    default: 'bg-muted/60 text-muted-foreground',
    primary: 'bg-primary/10 text-primary',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
  }[iconTone];

  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-border/60 bg-card">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${toneClass}`}>
          {icon}
        </div>
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
        </div>
      </div>
      <div>{right}</div>
    </div>
  );
}
